import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import FileView from "components/fileView";
import { StatusChip } from "components/StatusChip";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { Leaf } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateLegalResearchRequest } from "services/legalResearchService";
import { getResearchSchedules } from "services/researchScheduleService";
import {
    LEGAL_RESEARCH_REQUEST_STATUS
} from "utils/constantValues";
import { formatDate, formatDay, calculateRemainingDays } from "utils/helpers";
import ActionAvatars from "./actionAvatars";
import ManagementFeedback from "./managementFeedback";
import ResearchPresentationFeedback from "./researchPresentationFeedback";
import RespondBackForm from "./respondBackform";
import ScheduleDraftPresentationModal from "./scheduleDraftPresentationModal";
import SchedulePresentationModal from "./schedulePresentationModal";
import UploadResearchData from "./uploadResearchData";
import UploadResearchDraft from "./uploadResearchDraft";
import ConfirmationModal from "components/ConfirmationModal1";

interface Schedule {
    id: string;
    location: string;
    purpose: string;
    remark: string;
    research_request: string;
    scheduled_datetime: string;
}

function ResearchDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [selectedData, setSelectedData] = useState(0);
    const [schedules, setSchedules] = useState<Schedule | null>(null);
    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
    const [proposalFile, setProposalFile] = useState(null);
    const [formState, setFormState] = useState({
        ...request,
        urgency_level: request.urgency_level
            ? request.urgency_level.charAt(0).toUpperCase() +
            request.urgency_level.slice(1).toLowerCase()
            : "High",
    });
    const [implementationDirection, setImplementationDirection] = useState(formState?.implementation_direction ?? "");

    const [viewTeam, setViewTeam] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [isFeedbackFormOpen, setIsFeedBackFormOpen] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [showDataHistory, setShowDataHistory] = useState(false);
    const [showAmendedFileHistory, setShowAmendedFileHistory] = useState(false);
    const [showFeedbackFileHistory, setShowFeedbackFileHistory] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [pendingForwardStatus, setPendingForwardStatus] = useState<string>("");

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }
    const handleScheduleResearchClick = () => {
        setIsScheduleModalOpen(true);
    };
    const handleScheduleCloseModal = () => {
        setIsScheduleModalOpen(false);
    };
    const handleUploadDraftClick = () => {
        setIsDraftModalOpen(true);
    };
    const handleDraftCloseModal = () => {
        setIsDraftModalOpen(false);
    };
    const handleUploadDataClick = () => {
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const handleImplementationChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setImplementationDirection(event.target.value);
    };

    const handleResearchReject = async () => {
        const data = new FormData();
        data.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.draft_rejected.value
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchDraftRejected"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToRejectLegalResearchDraft"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleResearchApprove = async () => {
        setLoading(true);
        const data = new FormData();
        data.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchForwardedToDirectorSuccess"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToForwardLegalResearchToDirector"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDirectorApprove = async () => {
        setLoading(true);
        const data = new FormData();
        data.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value
        );
        data.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.sent_to_registrar.value
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchApprovedAndForwardedToRegistrar"), "success");
        } catch (error) {
            console.error("Error approving request:", error);
            showToast(t("FailedToApproveLegalResearch"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDirectorApproveToHead = async () => {
        setLoading(true);
        const data = new FormData();
        data.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value
        );
        data.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchApprovedAndForwardedToHead") || "Draft approved and forwarded to Head.", "success");
        } catch (error) {
            console.error("Error approving request:", error);
            showToast(t("FailedToApproveLegalResearch"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleImplementationSubmit = async () => {
        setLoading(true);
        const data = new FormData();

        data.append("implementation_direction", implementationDirection);

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState((prev: any) => ({
                ...prev, ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));
            showToast(t("LegalResearchDraftApproved"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToApproveLegalResearchDraft"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleForward = async () => {
        setConfirmLoading(true);

        const data = new FormData();
        data.append(
            "forward_status",
            pendingForwardStatus
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState((prev: any) => ({
                ...prev, ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));
            showToast(t("LegalResearchForwardedSuccess"), "success");
            setOpenConfirmModal(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    useEffect(() => {
        getResearchSchedules(contextData.token)
            .then((data) => {
                const filtered = data.filter(
                    (schedule: any) => (schedule.research_request === formState.id && schedule.purpose === "draft_presentation")
                );
                setSchedules(filtered[0]);
                console.log("draft filtered data:", filtered[0]);
            })
            .catch((error) => {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest"), "error");
            });
    }, [formState, isScheduleModalOpen]);

    useEffect(() => { }, [formState]);

    return (
        <div className="h-full overflow-scroll scrollbar-hide">
            {schedules &&
                (formState?.research_presentation_summary ||
                    formState?.documents?.find((document: any) => document.document_type === "draft_feedback" || document.document_type === "amended")) &&
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4 max-w-4xl">
                    {/* Official header */}
                    <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                    {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                </p>
                                <h3 className="text-white font-bold text-base truncate">{t("ResearchPresentationFeedback")}</h3>
                            </div>
                        </div>
                        {formState?.schedules?.find((schedule: any) => schedule.purpose === "draft_presentation") && (
                            <span className="flex-shrink-0 text-xs font-medium text-amber-100 bg-white/10 px-3 py-1 rounded-full">
                                {formatDate(formState?.schedules?.find((schedule: any) => schedule.purpose === "draft_presentation")?.scheduled_datetime)}
                            </span>
                        )}
                    </div>

                    {/* Body */}
                    <div className="p-5">
                        {formState?.research_presentation_summary && (
                            <div className="mb-4">
                                <p className="text-[11px] uppercase tracking-wide text-[#9B0505] font-semibold mb-1">{t("Summary")}</p>
                                <p className="text-[#3a3a3a] text-sm leading-relaxed whitespace-pre-line">{formState?.research_presentation_summary}</p>
                            </div>
                        )}

                        {/* Presentation feedback file(s): latest shown, previous in accordion */}
                        {(() => {
                            const feedbackDocs = (formState?.documents ?? [])
                                .filter((d: any) => d.document_type === "draft_feedback")
                                .slice()
                                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                            if (feedbackDocs.length === 0) return null;
                            const [latest, ...previous] = feedbackDocs;
                            return (
                                <div className="mb-4 rounded-lg border border-gray-200 p-3">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-2">{t("PresentationFeedback")}</p>
                                    <DocumentViewerModal
                                        documents={[latest]}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                    {previous.length > 0 &&
                                        <button type="button" onClick={() => setShowFeedbackFileHistory(!showFeedbackFileHistory)} className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#0A1D39] hover:underline">
                                            <svg className={`h-3 w-3 transition-transform ${showFeedbackFileHistory ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 8" fill="currentColor">
                                                <path d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z" />
                                            </svg>
                                            {t("PreviousFiles") || "Previous files"} ({previous.length})
                                        </button>}
                                    {showFeedbackFileHistory && previous.length > 0 &&
                                        <div className="border-t border-gray-200 mt-3 pt-3">
                                            <DocumentViewerModal
                                                documents={previous}
                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                            />
                                        </div>}
                                </div>
                            );
                        })()}

                        {/* Amended research file(s): latest shown, previous in accordion */}
                        {(() => {
                            const amendedDocs = (formState?.documents ?? [])
                                .filter((d: any) => d.document_type === "amended")
                                .slice()
                                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                            if (amendedDocs.length === 0) return null;
                            const [latest, ...previous] = amendedDocs;
                            return (
                                <div className="mb-4 rounded-lg border border-gray-200 p-3">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-2">{t("ResearchAmendedFile")}</p>
                                    <DocumentViewerModal
                                        documents={[latest]}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                    {previous.length > 0 &&
                                        <button type="button" onClick={() => setShowAmendedFileHistory(!showAmendedFileHistory)} className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#0A1D39] hover:underline">
                                            <svg className={`h-3 w-3 transition-transform ${showAmendedFileHistory ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 8" fill="currentColor">
                                                <path d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z" />
                                            </svg>
                                            {t("PreviousFiles") || "Previous files"} ({previous.length})
                                        </button>}
                                    {showAmendedFileHistory && previous.length > 0 &&
                                        <div className="border-t border-gray-200 mt-3 pt-3">
                                            <DocumentViewerModal
                                                documents={previous}
                                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                            />
                                        </div>}
                                </div>
                            );
                        })()}

                        {/* Implementation direction — management head input */}
                        {contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") &&
                            !formState?.implementation_direction &&
                            <form className="mt-4 pt-4 border-t border-gray-200">
                                <label htmlFor="remark" className="mb-2 block text-sm font-semibold text-[#0A1D39]">
                                    {t("ImplementationDirection")}
                                </label>
                                <textarea
                                    id="remark"
                                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent text-sm h-24 resize-y"
                                    placeholder={t("DetailHere")}
                                    required
                                    value={implementationDirection}
                                    onChange={handleImplementationChange}
                                ></textarea>
                                <div className="relative mt-3">
                                    <FileUpload onChange={handleFileChange} />
                                </div>
                                <button type="button" onClick={handleImplementationSubmit} className="py-3 mt-4 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold rounded-lg w-full flex items-center justify-center transition-colors" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t("Submitting")}
                                        </>
                                    ) : t("Submit")}
                                </button>
                            </form>}
                    </div>

                    {/* Footer action — forward buttons */}
                    {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                        formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value) &&
                        formState.implementation_direction &&
                        contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") &&
                        <div className="px-5 pb-5">
                            <button onClick={() => { setPendingForwardStatus(LEGAL_RESEARCH_REQUEST_STATUS.sent_to_registrar.value); setOpenConfirmModal(true); }} type="button" className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                {t("ForwardToRegistrar")}
                            </button>
                        </div>
                    }
                    {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                        formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
                        contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                        formState.research_presentation_summary &&
                        <div className="px-5 pb-5">
                            <button onClick={() => { setPendingForwardStatus(LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value); setOpenConfirmModal(true); }} type="button" className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                {t("ForwardToDirector")}
                            </button>
                        </div>
                    }
                    {/* Director — edit amended file + approve and forward to registrar */}
                    {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                        formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value) &&
                        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                        <div className="px-5 pb-5 flex flex-col sm:flex-row gap-3">
                            <button onClick={() => setIsFeedBackFormOpen(true)} type="button" className="flex-1 flex items-center justify-center gap-2 border border-[#0A1D39] text-[#0A1D39] hover:bg-[#0A1D39]/5 font-semibold px-4 py-2.5 rounded-lg transition-colors">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t("EditAmendedFile") || "Edit Amended File"}
                            </button>
                            <button onClick={() => { setPendingForwardStatus(LEGAL_RESEARCH_REQUEST_STATUS.sent_to_registrar.value); setOpenConfirmModal(true); }} type="button" className="flex-1 flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                {t("ApproveAndForwardToRegistrar") || "Approve & Forward to Registrar"}
                            </button>
                        </div>
                    }
                </div>}
            <div className="flex flex-col max-w-4xl">
                {schedules &&
                    (<div
                        onClick={() => (formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
                            contextData?.user?.roles?.some((p: string) => p === "GeneralAttorney")) ?
                            setIsScheduleModalOpen(!isScheduleModalOpen) : setIsScheduleModalOpen(false)}
                        className="border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4 cursor-pointer hover:shadow-md transition-shadow">
                        {/* Official header */}
                        <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                        {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                    </p>
                                    <h3 className="text-white font-bold text-base truncate">{t("ResearchDraftPresentation")}</h3>
                                </div>
                            </div>
                            {schedules?.scheduled_datetime &&
                                (() => {
                                    const days = calculateRemainingDays(schedules.scheduled_datetime);
                                    const label =
                                        days < 0
                                            ? t("OverdueByDays", { count: Math.abs(days) })
                                            : days === 0
                                                ? t("Today")
                                                : t("DaysLeft", { count: days });
                                    const cls =
                                        days < 0
                                            ? "bg-red-100 text-red-700"
                                            : days === 0
                                                ? "bg-orange-100 text-orange-700"
                                                : days <= 3
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : "bg-green-100 text-green-700";
                                    return (
                                        <span className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${cls}`}>
                                            {label}
                                        </span>
                                    );
                                })()}
                        </div>
                        {/* Body */}
                        <div className="p-4 bg-white flex items-center justify-between gap-4">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Schedule") || "Date"}</p>
                                {schedules?.scheduled_datetime ? (
                                    <p className="text-sm font-semibold text-[#0A1D39]">
                                        {formatDay(schedules?.scheduled_datetime).weekday}, {formatDay(schedules.scheduled_datetime).rest}
                                    </p>
                                ) : (
                                    <p className="text-sm text-gray-400">{t("DateNotScheduled")}</p>
                                )}
                            </div>
                            {schedules?.location && (
                                <div className="text-right">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Location")}</p>
                                    <p className="text-sm font-semibold text-[#0A1D39]">📍 {schedules?.location}</p>
                                </div>
                            )}
                        </div>
                        {/* Forward to Prosecutor — head, after scheduling */}
                        {formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                            contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
                                <div className="px-4 pb-4 bg-white">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPendingForwardStatus(LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value); setOpenConfirmModal(true); }}
                                        type="button"
                                        className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                        {t("ForwardToProsecutor")}
                                    </button>
                                </div>
                            )}
                        {/* Upload Presentation Feedback — team leader prosecutor, after scheduling.
                            Hidden once the presentation feedback has been submitted. */}
                        {contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
                            formState?.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                            !formState?.research_presentation_summary &&
                            !(formState?.documents ?? []).some((d: any) => d.document_type === "draft_feedback") && (
                                <div className="px-4 pb-4 bg-white">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setIsFeedBackFormOpen(!isFeedbackFormOpen); }}
                                        type="button"
                                        className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        {t("UploadPresentationFeedback")}
                                    </button>
                                </div>
                            )}
                    </div>)}
                {(formState?.documents?.find((document: any) => document.document_type === "draft" || document.document_type === "research_draft_file") || formState?.research_draft_file || formState?.draft_summary || (formState?.file_histories ?? []).some((fh: any) => fh.file_type === "draft")) &&
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4">
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-[#0A1D39] flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-wider text-[#9B0505] font-semibold">
                                        {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                    </p>
                                    <h3 className="font-bold text-[#0A1D39] text-base truncate">{t("ResearchDraft")}</h3>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {!schedules && contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator" || p === "LRDIFUDDirector") && (
                                    <button onClick={handleUploadDraftClick} type="button" className="flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        {t("EditDraft") || "Edit / Re-upload"}
                                    </button>
                                )}
                                {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value &&
                                    formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
                                    contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                                    <button onClick={() => { setPendingForwardStatus(LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value); setOpenConfirmModal(true); }} type="button" className="bg-[#0A1D39] hover:bg-[#13294d] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">{t("ForwardToCoordinator")}</button>
                                }
                            </div>
                        </div>
                        {formState?.draft_summary && (
                            <p className="text-sm text-gray-600 leading-relaxed mb-3">{formState?.draft_summary}</p>
                        )}
                        {(() => {
                            const draftDocs =
                                formState?.documents?.filter(
                                    (d: any) =>
                                        d.document_type === "draft" ||
                                        d.document_type === "research_draft_file",
                                ) ?? [];
                            const draftHistory = (formState?.file_histories ?? []).filter(
                                (fh: any) => fh.file_type === "draft",
                            );
                            const draftBaseUrl = `${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`;
                            if (draftDocs.length > 0) {
                                const orderedDocs = draftDocs
                                    .slice()
                                    .sort((a: any, b: any) =>
                                        String(b?.created_at ?? "").localeCompare(
                                            String(a?.created_at ?? ""),
                                        ),
                                    );
                                const latestDocs = [orderedDocs[0]];
                                const previousDocs = orderedDocs.slice(1);
                                return (
                                    <div>
                                        <DocumentViewerModal
                                            documents={latestDocs}
                                            baseUrl={draftBaseUrl}
                                        />
                                        {previousDocs.length > 0 && (
                                            <div className="mt-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowDraftHistory(!showDraftHistory)}
                                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                                                >
                                                    <svg className={`w-4 h-4 transition-transform ${showDraftHistory ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                    {t("PreviousDraftFiles") || "Previous draft files"} ({previousDocs.length})
                                                </button>
                                                {showDraftHistory && (
                                                    <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                        <DocumentViewerModal
                                                            documents={previousDocs}
                                                            baseUrl={draftBaseUrl}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            if (draftHistory.length > 0) {
                                const ordered = draftHistory.slice().reverse(); // newest first
                                const latest = ordered[0];
                                const previous = ordered.slice(1);
                                return (
                                    <div>
                                        <FileView
                                            title={t("ResearchDraft")}
                                            file={latest?.file}
                                            fileName={
                                                latest?.file &&
                                                latest.file.substring(
                                                    latest.file.lastIndexOf("/") + 1,
                                                )
                                            }
                                        />
                                        {previous.length > 0 && (
                                            <div className="mt-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowDraftHistory(!showDraftHistory)}
                                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                                                >
                                                    <svg className={`w-4 h-4 transition-transform ${showDraftHistory ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                    {t("PreviousDraftFiles") || "Previous draft files"} ({previous.length})
                                                </button>
                                                {showDraftHistory && (
                                                    <div className="mt-2 space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                        {previous.map((fh: any, i: number) => (
                                                            <FileView
                                                                key={i}
                                                                title={t("ResearchDraft")}
                                                                file={fh?.file}
                                                                fileName={
                                                                    fh?.file &&
                                                                    fh.file.substring(
                                                                        fh.file.lastIndexOf("/") + 1,
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            if (formState?.research_draft_file) {
                                return (
                                    <FileView
                                        title={t("ResearchDraft")}
                                        file={formState?.research_draft_file}
                                    />
                                );
                            }
                            return null;
                        })()}
                        {/* Coordinator draft actions (inside the Research Draft card) */}
                        {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
                            (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value ||
                                formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value) && (
                                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleRespondBackClick}
                                        disabled={loading}
                                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                                    >
                                        {t("RespondBack")}
                                    </button>
                                    <button
                                        onClick={handleResearchApprove}
                                        disabled={loading}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t("Approving")}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t("ApproveAndForwardToDirector")}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        {/* Director draft actions (inside the Research Draft card) */}
                        {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
                            (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value ||
                                formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value ||
                                formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value) && (
                                <>
                                    <RespondBackForm
                                        request={formState}
                                        setLegalResearch={setFormState}
                                        isOpen={isRespondBackFormOpen}
                                        onClose={() => setIsRespondBackFormOpen(false)}
                                        forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                        status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                                    />
                                    <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
                                        <button
                                            onClick={handleRespondBackClick}
                                            disabled={loading}
                                            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                                        >
                                            {t("RespondBack")}
                                        </button>
                                        <button
                                            onClick={handleDirectorApproveToHead}
                                            disabled={loading}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors disabled:opacity-50"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    {t("Approving")}
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    {t("ApproveAndForwardToHead") || "Approve and Forward to Head"}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                    </div>
                }
                {formState?.research_data_files?.length !== 0 &&
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4 mt-4">
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-[#0A1D39] flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-wider text-[#9B0505] font-semibold">
                                        {t("OfficeOfTheAttorneyGeneral") ||
                                            "Office of the Attorney General"}
                                    </p>
                                    <h3 className="font-bold text-[#0A1D39] text-base truncate">
                                        {t("ResearchData")}
                                        {formState?.research_data_files?.[selectedData]?.data_name
                                            ? ` — ${formState?.research_data_files?.[selectedData]?.data_name}`
                                            : ""}
                                    </h3>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {formState?.research_data_files?.length > 1 && (
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowDataHistory(!showDataHistory)}
                                            className="flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors"
                                        >
                                            {t("ViewResearchData")}
                                            <svg className={`h-4 w-4 transition-transform ${showDataHistory ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {showDataHistory &&
                                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] overflow-hidden">
                                                {formState?.research_data_files?.map((entry: any, idx: number) => (
                                                    <p
                                                        onClick={() => { setSelectedData(idx); setShowDataHistory(false); }}
                                                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${idx === selectedData ? "bg-[#0A1D39]/5 text-[#0A1D39] font-semibold" : "text-gray-700"}`}
                                                        key={idx}
                                                    >
                                                        {entry?.data_name}
                                                    </p>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                )}
                                {contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                                    formState?.prosecutor_assignments?.find((pr: any) => pr.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader && (
                                        <>
                                            <button onClick={() => setIsModalEditOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors">
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                {t("Edit")}
                                            </button>
                                        </>
                                    )}
                            </div>
                        </div>
                        {/* Summary */}
                        {formState?.research_data_files?.[selectedData]?.data_summary && (
                            <p className="text-sm text-gray-600 leading-relaxed mb-3">
                                {formState?.research_data_files?.[selectedData]?.data_summary}
                            </p>
                        )}
                        {/* Documents */}
                        {formState?.research_data_files?.[selectedData]?.research_data_documents && formState.research_data_files[selectedData].research_data_documents.length > 0 ? (
                            <DocumentViewerModal
                                documents={formState?.research_data_files?.[selectedData]?.research_data_documents}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/research-data-file-documents/document/`}
                            />
                        ) : (
                            <FileView title={t("ResearchData")} file={formState?.research_data_files?.[selectedData]?.datafile} />
                        )}
                        {/* Gathered By — data collectors (inside the Research Data card) */}
                        {(formState?.data_collection_entries?.length ?? 0) !== 0 &&
                            (<div className="flex items-center mt-4 pt-3 border-t border-gray-200">
                                <ActionAvatars
                                    title={t("GatheredBy")}
                                    users={(formState?.data_collection_entries ?? []).filter((collector: any) => collector.research_data_file === formState?.research_data_files?.[selectedData]?.id)}
                                    prosecutor={false}
                                />
                                {contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                                    formState?.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                                    <svg onClick={() => setIsModalEditOpen(true)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                                <svg onClick={() => setViewTeam(!viewTeam)} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                        <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                        <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                    </mask>
                                    <g mask="url(#mask0_9_31087)">
                                        <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                    </g>
                                </svg>
                            </div>)}
                    </div>}
                {((contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") || contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                    formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
                    (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value || formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value)) &&
                    <>
                        {
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            (<RespondBackForm
                                request={formState}
                                setLegalResearch={setFormState}
                                isOpen={isRespondBackFormOpen}
                                onClose={() => setIsRespondBackFormOpen(false)}
                                forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                            />)
                        }
                        {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ? (
                            <div className="mt-4 flex gap-2 font-medium text-base">
                                <button onClick={handleRespondBackClick} className="bg-gray-300 flex-1 text-black text-base py-2 px-8 rounded-lg ml-6" >{t("RespondBack")}</button>
                                <button onClick={handleDirectorApprove} className="bg-[#212121] hover:bg-[#141414] text-white rounded-lg flex-1 flex items-center justify-center py-2 px-4" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t("Approving")}
                                        </>
                                    ) : t("ApproveAndForwardToRegistrar")}
                                </button>
                            </div>
                        ) : null}
                    </>
                }
                {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") &&
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value) &&
                    !schedules &&
                    <>
                        {
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            (<RespondBackForm
                                request={formState}
                                setLegalResearch={setFormState}
                                isOpen={isRespondBackFormOpen}
                                onClose={() => setIsRespondBackFormOpen(false)}
                                forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                            />)
                        }
                        <div className="mt-4 mb-4 flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleRespondBackClick}
                                disabled={loading}
                                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                {t("RespondBack")}
                            </button>
                            <button
                                onClick={handleScheduleResearchClick}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold transition-colors disabled:opacity-50"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {t("ScheduleResearch")}
                            </button>
                        </div>
                    </>
                }
            </div>

            {(contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                formState?.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
                formState?.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.research_underway.value ||
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value ||
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value
                )) &&
                <div className="mt-4">
                    <div className="flex items-start gap-2 bg-[#0A1D39]/5 border border-[#0A1D39]/20 rounded-lg p-3 mb-3">
                        <svg className="w-5 h-5 text-[#0A1D39] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-[#0A1D39] leading-relaxed">
                            {t("ProsecutorDraftInstruction") ||
                                "As the team leader, insert the collected research data, then upload the research draft for review."}
                        </p>
                    </div>
                    <div className="flex gap-3 font-medium text-base">
                        <button onClick={handleUploadDataClick} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border border-[#0A1D39] text-[#0A1D39] bg-white font-semibold hover:bg-[#0A1D39]/5 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {t("InsertData")}
                        </button>
                        <button onClick={handleUploadDraftClick} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold shadow-sm transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {formState?.research_draft_file && <span>{t("Re")}</span>}
                            {t("UploadResearchDraft")}
                        </button>
                    </div>
                </div>}
            <UploadResearchData
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isModalEditOpen}
                onClose={() => setIsModalEditOpen(false)}
                researchData={formState.research_data_files?.[selectedData]}
            />
            <UploadResearchData
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
            <UploadResearchDraft
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isDraftModalOpen}
                onClose={handleDraftCloseModal}
            />
            <ScheduleDraftPresentationModal
                data={formState}
                setLegalResearch={setSchedules}
                isOpen={isScheduleModalOpen}
                onClose={handleScheduleCloseModal}
            />
            {(isFeedbackFormOpen && contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor" || p === "LRDIFUDDirector")) &&
                <ResearchPresentationFeedback
                    request={formState}
                    setLegalResearch={setFormState}
                    isOpen={isFeedbackFormOpen}
                    onClose={() => setIsFeedBackFormOpen(false)}
                />
            }
            {viewTeam &&
                <TeamView
                    teamMembers={(formState?.data_collection_entries ?? []).filter((collector: any) => collector.research_data_file === formState?.research_data_files?.[selectedData]?.id)}
                    onClose={() => setViewTeam(false)} />}

            <ConfirmationModal
                isOpen={openConfirmModal}
                title={t("ConfirmAction")}
                confirmText={t("YesContinue")}
                cancelText={t("Cancel")}
                isLoading={confirmLoading}
                onCancel={() => setOpenConfirmModal(false)}
                onConfirm={() => handleForward()}
            />
        </div>
    );
}

export default ResearchDraft;